
import { useEffect } from "react";

type KeyCombo = string;
type KeyHandler = (event: KeyboardEvent) => void;
type Options = {
  enableOnFormElements?: boolean;
};

/**
 * A hook for handling keyboard shortcuts
 * @param keyCombo Key combination (e.g., "mod+k", "shift+a")
 * @param callback Function to execute when the key combo is pressed
 * @param options Additional options
 */
export function useHotkeys(
  keyCombo: KeyCombo,
  callback: KeyHandler,
  options: Options = {}
) {
  useEffect(() => {
    const { enableOnFormElements = false } = options;
    
    const handler = (event: KeyboardEvent) => {
      // Skip if focused on form elements and not explicitly enabled
      if (
        !enableOnFormElements &&
        ["INPUT", "TEXTAREA", "SELECT"].includes((event.target as HTMLElement).tagName)
      ) {
        return;
      }
      
      const keys = keyCombo.toLowerCase().split("+");
      const modifierMap: Record<string, boolean> = {
        mod: event.metaKey || event.ctrlKey, // Platform agnostic modifier
        ctrl: event.ctrlKey,
        shift: event.shiftKey,
        alt: event.altKey,
        meta: event.metaKey,
      };
      
      // Check if the pressed key matches the last part of the combo (non-modifier)
      const mainKey = keys[keys.length - 1];
      const keyPressed = event.key.toLowerCase() === mainKey;
      
      // Check if all required modifiers are pressed
      const modifiersPressed = keys.slice(0, -1).every(modifier => modifierMap[modifier]);
      
      if (keyPressed && modifiersPressed) {
        event.preventDefault();
        callback(event);
      }
    };
    
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [keyCombo, callback, options]);
}
